import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { Loader } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const field = "w-full h-11 bg-white/[0.04] border border-white/10 px-4 text-sm outline-none focus:border-neon/60 transition-colors";
const label = "text-[11px] font-bold uppercase tracking-[0.2em] text-white/40";

const CreateBlog = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [f, setF] = useState<any>({ title: "", category: "awareness", summary: "", body: "", cover_image: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState<any>(isEdit ? null : false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/blogs/${id}`).then((r) => {
      const b = r.data;
      setSource(b);
      setF({
        title: b.title, category: b.category, summary: b.summary, body: b.body,
        cover_image: b.cover_image || "", tags: (b.tags || []).join(", "),
      });
    }).catch(() => setSource(false));
  }, [id, isEdit]);

  if (!user)
    return (
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-32" data-testid="blog-auth-gate">
        <h1 className="font-bebas text-5xl tracking-wide uppercase">Sign in to {isEdit ? "edit this piece" : "submit a piece"}</h1>
        <button onClick={login} className="mt-8 bg-neon px-8 h-12 text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">Join the circle</button>
      </div>
    );

  if (isEdit && source === null) return <Loader />;
  if (isEdit && source === false)
    return <div className="mx-auto max-w-[1400px] px-5 py-32 text-white/50" data-testid="blog-edit-not-found">Blog not found.</div>;
  if (isEdit && source.author_id !== user.user_id && user.role !== "admin")
    return (
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-32" data-testid="blog-edit-denied">
        <h1 className="font-bebas text-5xl tracking-wide uppercase">Not your piece</h1>
        <p className="mt-4 text-sm text-white/50">Only the author or an admin can edit this post.</p>
      </div>
    );

  const isAdminEditingOthers = isEdit && user.role === "admin" && source.author_id !== user.user_id;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        cover_image: f.cover_image || null,
        tags: f.tags.split(",").map((t: string) => t.trim()).filter(Boolean),
      };
      if (isEdit) {
        await api.put(`/blogs/${id}`, payload);
        toast.success(isAdminEditingOthers ? "Saved — live immediately. The author has been emailed." : "Blog updated.");
        navigate(`/blogs/${id}`);
      } else {
        const { data } = await api.post("/blogs", payload);
        toast.success("Submitted. Editors review every piece before publishing.");
        navigate(`/blogs/${data.blog_id}`);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 lg:px-10 py-16" data-testid="create-blog-page">
      <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">{isEdit ? "Edit" : "Contribute"}</span>
      <h1 className="mt-3 font-bebas text-5xl tracking-wide uppercase">{isEdit ? "Edit blog" : "Submit a blog"}</h1>
      {isAdminEditingOthers ? (
        <p className="mt-4 text-sm text-pcyellow" data-testid="admin-edit-notice">
          You're editing another author's piece as admin. Changes go live immediately and the author gets notified by email.
        </p>
      ) : (
        <p className="mt-4 text-sm text-white/50">{isEdit ? "Changes to an already-published piece are re-reviewed before they go live." : "Community writes, editors curate. You'll get an email when your piece is reviewed."}</p>
      )}

      <form onSubmit={submit} className="mt-10 grid gap-6">
        <div>
          <div className={label}>Title</div>
          <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="blog-title-input" className={`mt-2 ${field}`} />
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <div className={label}>Category</div>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="blog-category-select" className={`mt-2 ${field}`}>
              {["awareness", "explainer", "opinion", "research", "ground-report"].map((c) => <option key={c} value={c} className="bg-ink-surface">{c}</option>)}
            </select>
          </div>
          <div>
            <div className={label}>Tags (comma separated)</div>
            <input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="blog-tags-input" className={`mt-2 ${field}`} />
          </div>
        </div>
        <div>
          <div className={label}>Summary</div>
          <input required value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="blog-summary-input" className={`mt-2 ${field}`} />
        </div>
        <div>
          <div className={label}>Body</div>
          <textarea required rows={14} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="blog-body-input" className="mt-2 w-full bg-white/[0.04] border border-white/10 p-4 text-sm leading-relaxed outline-none focus:border-neon/60 transition-colors" />
        </div>
        <div>
          <ImageUpload value={f.cover_image} onChange={(url) => setF({ ...f, cover_image: url })} label="Cover photo (optional)" testid="blog-cover-upload" />
        </div>
        <button disabled={busy} data-testid="blog-submit-btn" className="h-12 bg-neon text-[12px] font-bold uppercase tracking-[0.2em] disabled:opacity-40 hover:pc-glow transition-shadow">
          {isEdit ? "Save changes" : "Submit for review"}
        </button>
      </form>
    </div>
  );
};

export default CreateBlog;
